import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as taskService from "source/models/services/taskService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskEditItemTaskPriority } from "source/models/task/taskEditItemTaskPriority";
import { IToggleButtonOption, ToggleButtons } from "source/components/framework/ToggleButtons";
import { ITaskEditItemTaskState } from "source/models/task/taskEditItemTaskState";
import { TaskStateListChip } from "../TaskStateListChip";
import { TreeCheckBoxDropdownItems } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown/TreeCheckboxDropdownItems";
import { ITreeListItem, TreeList } from "source/components/framework/List/TreeList";

/* eslint-disable no-unused-vars */
const enum TaskNewDialogPage {
    Visibility = "1",
    Unit = "2",
    Form = "3",
    State = "4",
    Priority = "5"
}
/* eslint-enable no-unused-vars */

export interface ITaskNewDialogProp {
    companyId: string;
    units: IIdTitleNode[];
    taskTypes: ITaskEditTypeItem[];
    taskPriorities: ITaskEditItemTaskPriority[];
    taskStates: ITaskEditItemTaskState[];
    taskTypeUnitIds: string[];
    unitId: string;
    taskTypeId: string;
    taskStateId: string;
    taskPriorityId: string;
    onOk: (unitId: string, taskTypeId: string, taskStateId: string, taskPriorityId: string, taskTypes: ITaskEditTypeItem[], taskStates: ITaskEditItemTaskState[], taskPriorities: ITaskEditItemTaskPriority[], taskTypeUnitIds: string[]) => void;
    onCancel: () => void;
}

export interface ITaskNewDialogState {
    page: TaskNewDialogPage;
    firstPage: TaskNewDialogPage;
    taskTypes: ITaskEditTypeItem[];
    taskPriorities: ITaskEditItemTaskPriority[];
    taskStates: ITaskEditItemTaskState[];
    taskTypeUnitIds: string[];
    visibilityOptions: IToggleButtonOption[];
    taskTypeTreeListItems: ITreeListItem[];
    isPublic: boolean;
    unitId: string;
    taskTypeId: string;
    taskStateId: string;
    taskPriorityId: string;
}

export class TaskNewDialog extends React.Component<ITaskNewDialogProp, ITaskNewDialogState> {
    static getVisibilityOptions = (taskTypes: ITaskEditTypeItem[]): IToggleButtonOption[] => {
        const result: IToggleButtonOption[] = [];
        if (taskTypes.find(i => i.isVisible(false))) {
            result.push({ id: "1", title: Translations.Public });
        }
        if (taskTypes.find(i => i.isVisible(true))) {
            result.push({ id: "0", title: Translations.Private });
        }
        return result;
    };

    static getTaskTypeTreeListItems = (taskTypes: ITaskEditTypeItem[], isPublic: boolean): ITreeListItem[] => {
        return TaskEditTypeItem.getTaskTypesForUiSelect(taskTypes, isPublic).map(i => {
            return {
                id: i.id,
                title: i.title,
                divider: i.divider,
                children: []
            };
        });
    };

    constructor(props: ITaskNewDialogProp) {
        super(props);
        const isPublic = !!props.unitId;
        const hasMultipleActiveUnits = IdTitleNode.getActiveCount(props.units) > 1;
        const visibilityOptions = TaskNewDialog.getVisibilityOptions(props.taskTypes);
        const hasMultipleVisibilityOptions = visibilityOptions.length > 1;
        const hasMultipleTaskTypes = props.taskTypes.length > 1;
        const hasPublicTaskTypes = props.taskTypes.filter(i => i.isVisible(false)).length > 0;
        const firstPage = hasMultipleVisibilityOptions
            ? TaskNewDialogPage.Visibility
            : hasMultipleActiveUnits && hasPublicTaskTypes
                ? TaskNewDialogPage.Unit
                : hasMultipleTaskTypes
                    ? TaskNewDialogPage.Form
                    : TaskNewDialogPage.State;
        const page = hasMultipleVisibilityOptions
            ? TaskNewDialogPage.Visibility
            : hasMultipleActiveUnits && isPublic
                ? TaskNewDialogPage.Unit
                : hasMultipleTaskTypes
                    ? TaskNewDialogPage.Form
                    : TaskNewDialogPage.State;
        this.state = {
            page,
            firstPage,
            isPublic,
            taskTypes: props.taskTypes,
            taskPriorities: props.taskPriorities,
            taskStates: props.taskStates,
            taskTypeUnitIds: props.taskTypeUnitIds,
            visibilityOptions,
            unitId: props.unitId,
            taskTypeId: props.taskTypeId,
            taskStateId: props.taskStateId,
            taskPriorityId: props.taskPriorityId,
            taskTypeTreeListItems: TaskNewDialog.getTaskTypeTreeListItems(props.taskTypes, isPublic)
        };
    }

    componentDidMount(): void {
        this.getTaskTypesBasedOnUnit(this.state.unitId);
    }

    getTaskPrioritiesAndTaskStatesBasedOnTaskType = async (taskTypeId: string) => {
        const props = this.props;
        const state = this.state;
        const result = await AppUtils.callService(() => taskService.getTaskEditTaskTypeData(props.companyId, taskTypeId), false, true);
        if (!result) return;
        const taskState = result.taskStates.find(i => i.id === state.taskStateId);
        const taskPriority = result.taskPriorities.find(i => i.id === state.taskPriorityId);
        const oldUnitId = state.unitId;
        let unitId = state.unitId;
        if (result.taskTypeUnitIds.length > 0) {
            unitId = result.taskTypeUnitIds.find(i => i === state.unitId) || result.taskTypeUnitIds[0];
        }
        this.setState({
            taskPriorities: result.taskPriorities,
            taskStates: result.taskStates,
            taskStateId: taskState?.id ?? result.taskStates[0].id,
            taskPriorityId: taskPriority?.id ?? result.taskPriorities[0].id,
            taskTypeUnitIds: result.taskTypeUnitIds
        });
        if (oldUnitId === unitId) return;
        this.handleChangeUnit(unitId);
    };

    handleChangeTaskType = (taskTypeId: string) => {
        this.setState({
            taskTypeId,
        });
        this.getTaskPrioritiesAndTaskStatesBasedOnTaskType(taskTypeId);
    };
    
    getTaskTypesBasedOnUnit = async (unitId: string) => {
        if (!unitId) return;
        const props = this.props;
        const state = this.state;
        const result = await AppUtils.callService(() => taskService.getTaskEditUnitData(props.companyId, unitId), false, true);
        if (!result) return;
        this.setState({
            taskTypes: result.taskTypes,
            taskTypeTreeListItems: TaskNewDialog.getTaskTypeTreeListItems(result.taskTypes, !!unitId)
        });
        const taskTypeId = result.taskTypes.find(i => i.id === state.taskTypeId)?.id ?? result.taskTypes[0].id;
        if (state.taskTypeId === taskTypeId) return;
        this.handleChangeTaskType(taskTypeId);
    };

    handleChangeUnit = (unitId: string) => {
        this.setState({
            unitId,
        });
        this.getTaskTypesBasedOnUnit(unitId);
    };

    handleChangePublic = (isPublic: boolean) => {
        const props = this.props;
        const state = this.state;
        const taskTypes = TaskEditTypeItem.getTaskTypesByVisibility(props.taskTypes, isPublic);
        const oldTaskTypeId = state.taskTypeId;
        const taskTypeId = taskTypes.find(i => i.id === oldTaskTypeId)?.id ?? taskTypes[0].id;
        this.setState({
            isPublic,
            taskTypeTreeListItems: TaskNewDialog.getTaskTypeTreeListItems(props.taskTypes, isPublic)
        });
        if (oldTaskTypeId === taskTypeId) return;
        this.handleChangeTaskType(taskTypeId);
    };

    getPreviousPageFromUnit = (): TaskNewDialogPage => {
        const state = this.state;
        const hasMultipleVisibilityOptions = state.visibilityOptions.length > 1;
        if (hasMultipleVisibilityOptions) {
            return TaskNewDialogPage.Visibility;
        }
        return null;
    };

    getPreviousPageFromForm = (): TaskNewDialogPage => {
        const props = this.props;
        const state = this.state;
        const hasMultipleActiveUnits = IdTitleNode.getActiveCount(props.units) > 1;
        if (state.isPublic && hasMultipleActiveUnits) {
            return TaskNewDialogPage.Unit;
        }
        return this.getPreviousPageFromUnit();
    };

    getPreviousPageFromState = (): TaskNewDialogPage => {
        const props = this.props;
        const hasMultipleTaskTypes = props.taskTypes.length > 1;
        if (hasMultipleTaskTypes) {
            return TaskNewDialogPage.Form;
        }
        return this.getPreviousPageFromForm();
    };

    handleMoveToPreviousPage = () => {
        const state = this.state;
        let newPage = state.page;
        if (state.page === TaskNewDialogPage.Priority) {
            newPage = TaskNewDialogPage.State;
        } else if (state.page === TaskNewDialogPage.State) {
            newPage = this.getPreviousPageFromState();
        } else if (state.page === TaskNewDialogPage.Form) {
            newPage = this.getPreviousPageFromForm();
        } else if (state.page === TaskNewDialogPage.Unit) {
            newPage = this.getPreviousPageFromUnit();
        }
        this.setState({
            page: newPage ?? state.page
        });
    };
    
    getNextPageFromUnit = (): TaskNewDialogPage => {
        const props = this.props;
        const hasMultipleTaskTypes = props.taskTypes.length > 1;
        if (hasMultipleTaskTypes) {
            return TaskNewDialogPage.Form;
        }
        return TaskNewDialogPage.State;
    };

    getNextPageFromVisibility = (): TaskNewDialogPage => {
        const props = this.props;
        const state = this.state;
        const hasMultipleActiveUnits = IdTitleNode.getActiveCount(props.units) > 1;
        if (state.isPublic && hasMultipleActiveUnits) {
            return TaskNewDialogPage.Unit;
        }
        return this.getNextPageFromUnit();
    };

    handleMoveToNextPage = () => {
        const state = this.state;
        let newPage = state.page;
        if (state.page === TaskNewDialogPage.Visibility) {
            newPage = this.getNextPageFromVisibility();
        } else if (state.page === TaskNewDialogPage.Unit) {
            newPage = this.getNextPageFromUnit();
        } else if (state.page === TaskNewDialogPage.Form) {
            newPage = TaskNewDialogPage.State;
        } else if (state.page === TaskNewDialogPage.State) {
            newPage = TaskNewDialogPage.Priority;
        }
        this.setState({
            page: newPage ?? state.page
        });
    };

    handleSave = async () => {
        const state = this.state;
        this.props.onOk(state.isPublic ? state.unitId : "", state.taskTypeId, state.taskStateId, state.taskPriorityId, state.taskTypes, state.taskStates, state.taskPriorities, state.taskTypeUnitIds);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="newTaskDialog"
                maxWidth="sm"
                maxHeight="600px"
                title={Translations.AddTask + " - " +
                    (state.page === TaskNewDialogPage.Visibility
                        ? Translations.SelectVisibility
                        : (state.page === TaskNewDialogPage.Unit
                            ? Translations.SelectUnit
                            : (state.page === TaskNewDialogPage.Form
                                ? Translations.SelectTaskType
                                : (state.page === TaskNewDialogPage.State
                                    ? Translations.SelectTaskState
                                    : Translations.SelectTaskPriority
                                )
                            )
                        )
                    )}
                body={
                    <Box display="flex" flexDirection="column" height="100%">
                        {state.page === TaskNewDialogPage.Visibility &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Visibility} fullWidth margin="0">
                                        <ToggleButtons
                                            value={state.isPublic ? "1" : "0"}
                                            options={state.visibilityOptions}
                                            onChange={(value) => this.handleChangePublic(value === "1")}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.page === TaskNewDialogPage.Unit &&
                            <FormField title={Translations.Unit} fullWidth margin="0">
                                <Box sx={{ height: "100%", overflowY: "auto" }} >
                                    <TreeCheckBoxDropdownItems
                                        selectedIds={[state.unitId]}
                                        singleSelect={true}
                                        showComment={true}
                                        values={props.units}
                                        onChange={(unitIds: string[]) => this.handleChangeUnit(unitIds[0])}
                                    />
                                </Box>
                            </FormField>
                        }
                        {state.page === TaskNewDialogPage.Form &&
                            <FormField title={Translations.TaskType} margin="0">
                                <Box sx={{ height: "100%", overflowY: "auto" }} >
                                    <TreeList
                                        height="100%"
                                        items={state.taskTypeTreeListItems}
                                        selectedId={state.taskTypeId}
                                        onSelect={item => this.handleChangeTaskType(item.id)}
                                    />
                                </Box>
                            </FormField>
                        }
                        {state.page === TaskNewDialogPage.State &&
                            <FormField title={Translations.State} margin="0">
                                <Box sx={{ height: "100%", overflowY: "auto" }} >
                                    <TreeList
                                        height="100%"
                                        items={state.taskStates.map(item => {
                                            return {
                                                id: item.id,
                                                title: <TaskStateListChip code={item.code} color={item.color} fullWidth={true} />,
                                                children: []
                                            };
                                        })}
                                        selectedId={state.taskStateId}
                                        onSelect={item => this.setState({ taskStateId: item.id })}
                                    />
                                </Box>
                            </FormField>
                        }
                        {state.page === TaskNewDialogPage.Priority &&
                            <FormField title={Translations.Priority} margin="0">
                                <Box sx={{ height: "100%", overflowY: "auto" }} >
                                    <TreeList
                                        height="100%"
                                        items={state.taskPriorities.map(item => {
                                            return {
                                                id: item.id,
                                                title: item.code,
                                                children: []
                                            };
                                        })}
                                        selectedId={state.taskPriorityId}
                                        onSelect={item => this.setState({ taskPriorityId: item.id })}
                                    />
                                </Box>
                            </FormField>
                        }
                    </Box>
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: props.onCancel },
                    {
                        title: Translations.Previous,
                        variant: "outlined",
                        onClick: state.page === state.firstPage ? null : () => this.handleMoveToPreviousPage()
                    },
                    {
                        title: Translations.Next,
                        variant: "confirmation",
                        onClick: state.page !== TaskNewDialogPage.Priority ? () => this.handleMoveToNextPage() : this.handleSave
                    },
                ]}
                onClose={props.onCancel}
            />
        );
    }
}